import NumberFormat, { NumberFormatValues } from 'react-number-format';
import { FocusEvent, ForwardedRef, forwardRef, useState } from 'react';
import { useIntl } from 'react-intl';

interface EventStub {
    target: {
        id: string;
        name: string;
        value?: number;
    };
}

interface NumberFormatCustomProps {
    id: string;
    name: string;
    fixedDecimalScale?: boolean;
    decimalScale?: number;
    onChange: (event: EventStub) => void;
    onBlur: (event: EventStub) => void;
    noFractionalNumbers?: boolean;
    noNegativeNumbers?: boolean;
    placeholder?: string;
    prefix?: string;
    suffix?: string;
    hasThousandSeparator?: boolean;
    /**
     * Pads the shown value to at least this many decimals once the field is
     * left: 0 reads "0,00", 12.5 reads "12,50", 89.1234 stays. Only what is
     * shown changes, never the value; an empty field stays empty. Amounts,
     * prices and percentages set 2, quantities leave it out (LST-S20, R-269).
     */
    minDecimalScale?: number;
    onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
}

/** How many decimals a value already has: 12.5 has one, 89.1234 four. */
const decimalsOf = (value: unknown): number => {
    const text = null === value || undefined === value ? '' : String(value);

    return text.includes('.') ? text.split('.')[1].length : 0;
};

function NumericTextfieldRef(props: NumberFormatCustomProps, ref: ForwardedRef<NumberFormat<NumberFormatCustomProps>>) {
    const {
        id,
        name,
        onChange,
        onBlur,
        fixedDecimalScale,
        decimalScale,
        noFractionalNumbers,
        noNegativeNumbers,
        placeholder,
        hasThousandSeparator = true,
        minDecimalScale,
        onFocus,
        ...other
    } = props;

    const intl = useIntl();
    const decimalNumber = 1000.1;
    const parts = intl.formatNumberToParts(decimalNumber);
    // HINT: when mergin with theme-next, don't forget to add thousandSeparator to the props
    const thousandSeparator = hasThousandSeparator ? parts.find(part => part.type === 'group')?.value ?? '.' : false;
    const decimalSeparator = parts.find(part => part.type === 'decimal')?.value ?? ',';

    const handleValueChange = (values: NumberFormatValues) => {
        const value = values.floatValue;
        onChange({ target: { id, name, value } });
    };

    const handleBlur = () => {
        setFocused(false);
        onBlur({ target: { id, name } });
    };

    // While someone types, the figure stays as typed; padding it then would
    // move the caret (#2239 R-140).
    const [focused, setFocused] = useState(false);
    const shown = (other as { value?: unknown }).value;
    const padded =
        !focused &&
        !noFractionalNumbers &&
        (minDecimalScale ?? 0) > 0 &&
        '' !== shown &&
        null !== shown &&
        undefined !== shown;

    return (
        <NumberFormat
            {...other}
            id={id}
            name={name}
            getInputRef={ref}
            thousandSeparator={thousandSeparator}
            decimalSeparator={decimalSeparator}
            decimalScale={
                padded ? Math.max(minDecimalScale ?? 0, decimalsOf(shown)) : noFractionalNumbers ? 0 : decimalScale
            }
            fixedDecimalScale={padded || fixedDecimalScale}
            onFocus={(event: FocusEvent<HTMLInputElement>) => {
                setFocused(true);
                onFocus?.(event);
            }}
            allowNegative={!noNegativeNumbers}
            onValueChange={handleValueChange}
            onBlur={handleBlur}
            placeholder={placeholder || '0'}
        />
    );
}

export const NumericTextfield = forwardRef(NumericTextfieldRef);
